import {
  type SignIn,
  type ExperienceSocialConnector,
  AgreeToTermsPolicy,
  SignInIdentifier,
} from '@logto/schemas';
import { useMemo } from 'react';

import IdentifierSignInForm from '@/components/IdentifierSignInForm';
import PasswordSignInForm from '@/components/PasswordSignInForm';
import SocialSignInList from '@/containers/SocialSignInList';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import usePlatform from '@/hooks/use-platform';
import useTerms from '@/hooks/use-terms';

import useIdentifierSignInMethods from '../IdentifierSignIn/use-identifier-sign-in-methods';

type Props = {
  readonly signInMethods: SignIn['methods'];
  readonly socialConnectors: ExperienceSocialConnector[];
};

const Main = ({ signInMethods, socialConnectors }: Props) => {
  const { agreeToTermsPolicy } = useTerms();
  const { isPasswordOnly } = useIdentifierSignInMethods();
  const { isMobile } = usePlatform();
  // Autofocusing on mobile would force the keyboard open over the layout.
  const shouldAutoFocus = !isMobile;

  // Phone gets its own "Continue with phone" button, so it leaves the inline field unless
  // it is the only method.
  const inlineSignInMethods = useMemo(() => {
    const nonPhone = signInMethods.filter(
      ({ identifier }) => identifier !== SignInIdentifier.Phone
    );
    return nonPhone.length > 0 ? nonPhone : signInMethods;
  }, [signInMethods]);

  if (signInMethods.length === 0 && socialConnectors.length > 0) {
    return (
      <div className="flex flex-col gap-4">
        <SocialSignInList socialConnectors={socialConnectors} />
        {agreeToTermsPolicy === AgreeToTermsPolicy.Manual && <TermsAndPrivacyCheckbox />}
      </div>
    );
  }

  if (isPasswordOnly) {
    return (
      <PasswordSignInForm
        autoFocus={shouldAutoFocus}
        signInMethods={inlineSignInMethods.map(({ identifier }) => identifier)}
      />
    );
  }

  if (inlineSignInMethods.length > 0) {
    return <IdentifierSignInForm autoFocus={shouldAutoFocus} signInMethods={inlineSignInMethods} />;
  }

  return null;
};

export default Main;
